// src/services/whatsappService.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axiosService from './axiosService';
import appService from './appService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import { logErrorSilent } from 'utils/errorLogger';
import { returnOnlyNumbers } from 'utils/helpers';
import { mergeUrlParamsQuery } from 'utils/urlUtils';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// SERVICE METHODS
// *******************************************************************************
const whatsappService = {};

whatsappService.serviceStatus = async () => {
  whatsappService.STATUS = 'offline';
};

// Send a WhatsApp message to a specific number
whatsappService.sendWhatsAppMessage = async (session, number, message) => {
  if (whatsappService.STATUS !== 'online') return;

  try {
    const url = mergeUrlParamsQuery({
      url: API.WHATSAPP.SEND_MESSAGE,
      params: { session, number, message },
    });

    await axiosService.get({ url });

    appService.notification({ flag: 'success', title: translateX('whatsapp_test_success') });
  } catch (error) {
    appService.notification({ flag: 'error', title: translateX('whatsapp_test_error') });
    throw error;
  }
};

// Create a new WhatsApp client connection
whatsappService.postClientConnection = async (body, subscriptionId) => {
  if (whatsappService.STATUS !== 'online') return;

  try {
    await axiosService.post({
      url: API.WHATSAPP.URL,
      data: body,
      headers: { 'x-subscriptionid': subscriptionId },
    });

    appService.notification({ flag: 'success', title: translateX('whatsapp_connected_success') });
  } catch (error) {
    appService.notification({ flag: 'error', title: translateX('whatsapp_connected_error') });
    throw error;
  }
};

// Edit an existing WhatsApp client connection
whatsappService.editClientConnection = async (body, _id) => {
  if (whatsappService.STATUS !== 'online') return;

  try {
    const url = mergeUrlParamsQuery({
      url: API.WHATSAPP.URL,
      params: { _id },
    });

    await axiosService.put({ url, data: body });

    appService.notification({ flag: 'success', title: translateX('whatsapp_edit_success') });
  } catch (error) {
    appService.notification({ flag: 'error', title: translateX('whatsapp_edit_error') });
    throw error;
  }
};

// Get paginated list of WhatsApp connections
whatsappService.getWhatsAppList = async subscriptionId => {
  if (whatsappService.STATUS !== 'online') return;

  try {
    return await axiosService.get({
      url: API.WHATSAPP.PAGINATION,
      headers: { 'x-subscriptionid': subscriptionId },
    });
  } catch (error) {
    throw error;
  }
};

// Delete a WhatsApp connection
whatsappService.deleteWhatsapp = async _id => {
  if (whatsappService.STATUS !== 'online') return;

  try {
    const url = mergeUrlParamsQuery({
      url: API.WHATSAPP.URL,
      params: { _id },
    });

    await axiosService.delete({ url });
  } catch (error) {
    logErrorSilent(error, {
      context: 'whatsappService.deleteWhatsapp',
      extraData: { _id },
    });
  }
};

// Check if a phone number has WhatsApp (works with mobile and landline/WhatsApp Business)
whatsappService.hasWhatsApp = async (phoneNumber, countryCode = 'BR') => {
  try {
    if (!phoneNumber) return false;

    // Clean phone number (remove non-numeric characters)
    const cleanNumber = returnOnlyNumbers(phoneNumber);
    if (!cleanNumber || cleanNumber.length < 10) return false;

    // Build E.164 format number with country code
    const cc = (countryCode || 'BR').toUpperCase();
    const dialCodeMap = {
      BR: '55',
      PT: '351',
      US: '1',
      CA: '1',
      ES: '34',
      GB: '44',
    };
    const dialCode = dialCodeMap[cc] || '55';

    // If number already starts with dial code, use as-is
    let e164Number = cleanNumber.startsWith(dialCode) ? cleanNumber : `${dialCode}${cleanNumber}`;

    const url = mergeUrlParamsQuery({
      url: API.WHATSAPP.VALIDATE_NUMBER,
      query: { phoneNumber: e164Number },
    });

    const [response] = await axiosService.get({ url, returnAsObject: true });

    // Extract result from response - handle both correct (data.numbers) and legacy (data.data.numbers) structures
    // Priority: data.numbers (correct) > data.data.numbers (legacy/wrong backend structure)
    const numbers = response?.data?.numbers || response?.data?.data?.numbers || response?.numbers || [];

    // Also check if response.data itself contains the result directly
    const directResult = response?.data?.hasWhatsapp ?? response?.data?.exists ?? response?.data?.registered;

    // If direct result exists, use it
    if (typeof directResult === 'boolean') {
      return directResult;
    }

    // Otherwise look in numbers array - try multiple property names for phone number
    // Note: API may return number with country code (5519982320000) while we have without (19982320000)
    const result = numbers.find(n => {
      const numberFromResponse = returnOnlyNumbers(n.phoneNumber || n.number || n.jid || n.phone || '');
      // Match if exact, or if response number ends with our number (handles country code prefix)
      const exactMatch = numberFromResponse === cleanNumber;
      const endsWithMatch = numberFromResponse.endsWith(cleanNumber);
      return exactMatch || endsWithMatch;
    });

    // Also check for exists/registered property names
    const hasWA = result?.hasWhatsapp ?? result?.exists ?? result?.registered ?? result?.numberExists;

    return hasWA === true;
  } catch (error) {
    console.error('[whatsappService.hasWhatsApp] Error:', error);
    // Return false on error to avoid blocking the flow
    return false;
  }
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default whatsappService;

// *******************************************************************************
// HELPERS
// *******************************************************************************
